import * as React from 'react';

import debounce from 'lodash/debounce';

/*
This component will only work in a react 16+ app. It defers or stops the wrapped component
from rehydrating during CSR, saving the page from doing work it doesn't need to. 
Currently it is only enabled on xdp-perf.
Reference for deferring rehydration https://github.com/facebook/react/issues/10923#issuecomment-338715787.
*/

type Props = {
  isElementInViewport?: boolean;
  shouldHydrate?: boolean; // Use this to either toggle hydration from a state variable or mark as false to never rehydrate (ssr only)
  whenVisible?: boolean; // rehydrate when component is visible
  children: React.ReactNode;
};

type State = {
  isElementInViewport: boolean;
};

const isClient =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.document.createElement !== 'undefined';

class LazyHydrate extends React.Component<Props, State> {
  childRef = React.createRef<HTMLDivElement>();

  state = {
    isElementInViewport: false,
  };

  constructor(props: Props) {
    super(props);

    this.childRef = React.createRef();
  }

  componentDidMount() {
    const { whenVisible } = this.props;

    if (this.childRef.current && whenVisible) {
      if ('IntersectionObserver' in window) {
        const observer = this.createIntersectionObserver();
        observer.observe(this.childRef.current);
      } else {
        // Fallback to window scroll listener
        this.handleScrollEventListener();
        // @ts-expect-error TSMIGRATION-4.9 TS2339
        window.addEventListener('scroll', debounce(this.handleScrollEventListener, 100, { leading: true }));
      }
    }
  }

  componentWillUnmount() {
    if (this.childRef.current) {
      window.removeEventListener('scroll', debounce(this.handleScrollEventListener, 100, { leading: true }));
    }
  }

  handleScrollEventListener = () => {
    if (!this.childRef.current) {
      return null;
    }

    const { top } = this.childRef.current.getBoundingClientRect();

    // Check if element is about to be visible in the viewport
    const isVisible = top <= window.pageYOffset + window.innerHeight;

    if (isVisible) {
      this.setIsElementInViewport();
    }
  };

  setIsElementInViewport = () => {
    const { isElementInViewport } = this.state;
    // We only want to set this to true once, after it rehydrates we don't care about it's position
    if (!isElementInViewport) {
      this.setState(() => ({
        isElementInViewport: true,
      }));
      window.removeEventListener('scroll', debounce(this.handleScrollEventListener, 100, { leading: true }));
    }
  };

  createIntersectionObserver = () => {
    // 0 threshold means the callback will trigger when 1px of the element is in view
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    };

    return new IntersectionObserver(([entry]) => {
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      if (entry.isIntersecting) {
        this.setIsElementInViewport();
      }
    }, options);
  };

  render() {
    const { shouldHydrate, whenVisible, children } = this.props;
    const { isElementInViewport } = this.state;

    const rehydrateIfInView = whenVisible && isElementInViewport;

    const hydrate = !isClient || rehydrateIfInView || (shouldHydrate && !whenVisible);

    if (hydrate) {
      // render/rehydrate
      return <div ref={this.childRef}>{children}</div>;
    } else {
      /* 
        When react encounters an element setting inner HTML it will not re-render the node.
        So on server we render the element and on CSR we leave the HTML alone by returning 
        the following element. 
      */

      // Don't rehydrate
      // eslint-disable-next-line react/no-danger
      return <div ref={this.childRef} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: '' }} />;
    }
  }
}

export default LazyHydrate;
